import { Pipe, PipeTransform } from '@angular/core';
import { LangueService } from 'src/app/services/langue/langue.service';
import { TranslateService } from '@ngx-translate/core';
import { AdresseClient } from '../api/model/adresse-client.model';
import { LangueEnum } from '../constantes/Langue.enum';
import { AdressePhysiqueCredit } from '../api/model/adresse-physique-credit.model';
import { AdressePhysiqueBancaire } from '../api/model/adresse-physique-bancaire.model';

@Pipe({
  name: 'adresse',
  standalone: false,
})
export class AdressePipe implements PipeTransform {
  isFrancais: boolean;
  trail: string = '';
  private readonly regCodePostalByGroup: RegExp = /([^-\s]{3})(\s{0,1}?)([^-\s]{3})/;
  private readonly maskCodePostal: string = '$1 $3';
  private readonly appartementFr: string = 'appartement';
  private readonly appartementEn: string = 'Apartment';

  constructor(
    private readonly langueService: LangueService,
    readonly translateService: TranslateService
  ) {}

  transform(coordonnees: AdresseClient | AdressePhysiqueCredit | AdressePhysiqueBancaire): string {
    if (!coordonnees) {
      return '';
    }
    this.isFrancais = this.langueService.getCurrentLanguage() === LangueEnum.FR;
    this.trail = this.isFrancais ? '' : ',';
    let adresse: string = '';
    if ('numeroCiviqueNomRue' in coordonnees || 'numeroCivique' in coordonnees) {
      adresse = this.formatAdressePhysiqueBancaire(coordonnees as AdressePhysiqueBancaire);
    } else if ('texteAdresseLigne1' in coordonnees) {
      adresse = this.formatAdressePhysiqueCredit(coordonnees as AdressePhysiqueCredit);
    } else {
      adresse = this.formatAdresseClient(coordonnees as AdresseClient);
    }
    return this.finalClean(adresse);
  }

  private formatAdresseClient(coordonnees: AdresseClient): string {
    return `${this.cleanAdresse(coordonnees.adresseLigne1, coordonnees.adresseLigne2, coordonnees.adresseLigne3)}\n${this.clean(coordonnees.nomMunicipalite, this.trail)} ${this.cleanProvince(coordonnees.codeSubdivisionPays, this.isFrancais)}  ${this.cleanPostal(coordonnees.codePostal)}`.trim();
  }

  private formatAdressePhysiqueCredit(coordonnees: AdressePhysiqueCredit): string {
    return `${this.cleanAdresse(coordonnees.texteAdresseLigne1, coordonnees.texteAdresseLigne2, coordonnees.texteAdresseLigne3)}\n${this.clean(coordonnees.nomMunicipalite, this.trail)} ${this.cleanProvince(coordonnees.codeSubdivisionPays, this.isFrancais)}  ${this.cleanPostal(coordonnees.codePostal)}`.trim();
  }

  private formatAdressePhysiqueBancaire(coordonnees: AdressePhysiqueBancaire): string {
    return `${this.cleanAdresseBancaire(coordonnees)}\n${this.clean(coordonnees.nomMunicipalite, this.trail)} ${this.cleanProvince(coordonnees.codeSubdivisionPays, this.isFrancais)}  ${this.cleanPostal(coordonnees.codePostal)}`.trim();
  }

  cleanAdresse(adresseLigne1: string, adresseLigne2: string, adresseLigne3: string): string {
    let adresse;
    if (adresseLigne1 && this.isFrancais) {
      adresseLigne1 = adresseLigne1.trim();
      const numero = adresseLigne1.substring(0, adresseLigne1.indexOf(' '));
      const rue = adresseLigne1.substring(adresseLigne1.indexOf(' ') + 1);
      adresse = numero ? numero + ', ' + rue : rue;
    } else {
      adresse = adresseLigne1 ? adresseLigne1 : '';
    }
    adresse += adresseLigne2 ? ' ' + adresseLigne2 : '';
    adresse += adresseLigne3 ? ' ' + adresseLigne3 : '';
    return adresse;
  }

  cleanAdresseBancaire(coordonnees: AdressePhysiqueBancaire): string {
    let adresse;
    if (coordonnees.numeroCivique || coordonnees.nomRue) {
      adresse = coordonnees.numeroCivique
        ? coordonnees.numeroCivique + (this.isFrancais ? ', ' : ' ') + coordonnees.nomRue
        : coordonnees.nomRue;
    } else if (coordonnees.numeroCiviqueNomRue) {
      adresse = coordonnees.numeroCiviqueNomRue;
    }
    adresse += coordonnees.numeroUnite ? ', ' + this.cleanUnite(coordonnees.numeroUnite) : '';
    return adresse;
  }

  cleanProvince(value: string, isFrancais: boolean = false): string {
    let province = this.translateService.instant(`formulairePosteCanada.donneeReference.provinces.CA-${value}`);
    if (!province || province === `formulairePosteCanada.donneeReference.provinces.CA-${value}`) {
      province = this.translateService.instant(`formulairePosteCanada.donneeReference.provinces.${value}`); // Bancaire retour CA-QC au lieu de QC
      if (!province || province === `formulairePosteCanada.donneeReference.provinces.${value}`) {
        value = value?.includes('-') ? value.substring(value.indexOf('-') + 1).trim() : value;
        province = value || '';
      }
    }
    return !province ? '' : isFrancais ? `(${province})` : province;
  }

  cleanUnite(numeroUnite: string): string {
    return this.isFrancais ? this.appartementFr + ' ' + numeroUnite : this.appartementEn + ' ' + numeroUnite;
  }

  cleanPostal(value: string): string {
    if (!value) {
      return '';
    }
    value = value.replace(this.regCodePostalByGroup, this.maskCodePostal);
    return value;
  }

  clean(value: string, trail: string = ''): string {
    return value ? value + trail : '';
  }

  finalClean(value: string): string {
    if (value.endsWith(',')) {
      value = value.slice(0, -1).trim();
    }
    return value;
  }
}
